'use client';

import { c as _c } from "react/compiler-runtime";
import { getAriaLabel } from '@bridge/utils';
import { FieldShell } from './FieldShell';
import { getFieldIds } from './utils';
import type { ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';
import type { BaseComponentProps, BaseFieldProps } from './types';
import type { FieldIds } from './utils';

/**
 * Render function type for CustomField.
 * Receives field props, field state, disabled status, and the field ids.
 */
export type CustomFieldRenderFn<TFieldValues extends FieldValues> = (props: {
  field: BaseFieldProps<TFieldValues>['field'];
  fieldState: BaseFieldProps<TFieldValues>['fieldState'];
  disabled: boolean;
  /**
   * Ids of the label, description and error the shell renders around this
   * field. A render function whose control is a real form element should point
   * `aria-labelledby` / `aria-describedby` at them, since those elements refer
   * to the wrapping container rather than to the control inside it.
   */
  ids: FieldIds;
}) => ReactNode;

/**
 * Component props for CustomField.
 * The render function provides complete flexibility for custom UI inside FormControl.
 */
export type CustomComponentProps<TFieldValues extends FieldValues = FieldValues> = BaseComponentProps<'div'> & {
  render: CustomFieldRenderFn<TFieldValues>;
};
interface CustomFieldProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  componentProps: CustomComponentProps<TFieldValues>;
}

/**
 * Generic custom field component that handles all form boilerplate
 * while allowing complete customization of the input UI via render prop.
 *
 * @example
 * {
 *   name: 'myField',
 *   component: 'custom',
 *   label: 'My Field',
 *   componentProps: {
 *     render: ({ field, disabled }) => (
 *       <button onClick={() => field.onChange('value')} disabled={disabled}>
 *         Custom UI
 *       </button>
 *     )
 *   }
 * }
 */
export function CustomField(t0) {
  const $ = _c(40);
  const {
    field,
    fieldConfig,
    fieldState,
    componentProps,
    labelSize
  } = t0;
  let componentDisabled;
  let id;
  let render;
  let restComponentProps;
  if ($[0] !== componentProps) {
    ({
      id,
      disabled: componentDisabled,
      render,
      ...restComponentProps
    } = componentProps);
    $[0] = componentProps;
    $[1] = componentDisabled;
    $[2] = id;
    $[3] = render;
    $[4] = restComponentProps;
  } else {
    componentDisabled = $[1];
    id = $[2];
    render = $[3];
    restComponentProps = $[4];
  }
  const isDisabled = Boolean(field.disabled || componentDisabled);
  let T0;
  let t1;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] !== field || $[6] !== fieldConfig || $[7] !== fieldState || $[8] !== id || $[9] !== isDisabled || $[10] !== labelSize || $[11] !== render || $[12] !== restComponentProps) {
    const ids = getFieldIds(fieldConfig, fieldState, id);
    T0 = FieldShell;
    t7 = fieldConfig;
    t8 = fieldState;
    t9 = ids;
    t10 = labelSize;
    t1 = restComponentProps;
    t2 = ids.describedBy;
    t3 = ids.isInvalid;
    if ($[24] !== fieldConfig.label) {
      t4 = getAriaLabel(fieldConfig.label);
      $[24] = fieldConfig.label;
      $[25] = t4;
    } else {
      t4 = $[25];
    }
    t5 = ids.fieldId;
    t6 = render({
      field,
      fieldState,
      disabled: isDisabled,
      ids
    });
    $[5] = field;
    $[6] = fieldConfig;
    $[7] = fieldState;
    $[8] = id;
    $[9] = isDisabled;
    $[10] = labelSize;
    $[11] = render;
    $[12] = restComponentProps;
    $[13] = T0;
    $[14] = t1;
    $[15] = t10;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    T0 = $[13];
    t1 = $[14];
    t10 = $[15];
    t2 = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  let t11;
  if ($[26] !== t1 || $[27] !== t2 || $[28] !== t3 || $[29] !== t4 || $[30] !== t5 || $[31] !== t6) {
    t11 = <div {...t1} aria-describedby={t2} aria-invalid={t3} aria-label={t4} id={t5}>{t6}</div>;
    $[26] = t1;
    $[27] = t2;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== T0 || $[34] !== t10 || $[35] !== t11 || $[36] !== t7 || $[37] !== t8 || $[38] !== t9) {
    t12 = <T0 fieldConfig={t7} fieldState={t8} ids={t9} labelSize={t10}>{t11}</T0>;
    $[33] = T0;
    $[34] = t10;
    $[35] = t11;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  return t12;
}